import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import { determineFormFieldIndexes } from "@midland-industries/Utilities/DetermineFormFieldIndexes";
import { determineFormIndex } from "@midland-industries/Utilities/DetermineFormIndex";
import { DynamicFormContext } from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    key = "key",
    hasLabel = "hasLabel",
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
    options = "options",
}

interface OptionModel {
    fields: {
        label: string;
        value: string;
    };
}

interface OwnProps {
    handleOnChange?: React.ChangeEventHandler<HTMLSelectElement>;
    fields: {
        [fields.key]: string;
        [fields.hasLabel]: boolean;
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
        [fields.options]: OptionModel[];
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    return {
        dynamicFormData,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DynamicFormSelectStyles {
    wrapper?: InjectableCss;
    topicsSelect?: SelectPresentationProps;
}

export const dynamicFormSelectStyles: DynamicFormSelectStyles = {
    wrapper: {
        css: css`
            width: 100%;
            align-items: center;
        `,
    },
};

export const styles = dynamicFormSelectStyles;

const DynamicFormSelect: React.FC<Props> = ({ fields, setDynamicFormFieldValue, handleOnChange, dynamicFormData }) => {
    const { key, hasLabel, label, hintText, options, isRequired } = fields;

    const defaultSelectValue = "";

    const [selectValue, setSelectValue] = useState(defaultSelectValue);
    const [selectErrorMessage, setSelectErrorMessage] = useState("");
    const [fieldIndex, setFieldIndex] = useState(-1);
    const [formIndex, setFormIndex] = useState(-1);

    const dynamicFormContext = useContext(DynamicFormContext);

    useEffect(() => {
        initializeSelectWidget();
    }, [dynamicFormData?.forms]);

    useEffect(() => {
        const formName = dynamicFormContext.formName;
        const { isValid, errorMessage } = ValidateSelectValue();
        setDynamicFormFieldValue({
            formName,
            formField: {
                key,
                group: "",
                label,
                isRequired,
                value: selectValue || defaultSelectValue,
                type: "DynamicFormSelect",
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
    }, [selectValue]);

    useEffect(() => {
        if (selectValue !== dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value) {
            if (dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value === undefined) {
                setSelectValue(defaultSelectValue);
            } else {
                setSelectValue(dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value);
            }
        }
    }, [dynamicFormData?.forms[formIndex]?.fields[fieldIndex]]);

    const initializeSelectWidget = () => {
        const formFieldIndexes = determineFormFieldIndexes(dynamicFormContext.formName, key, dynamicFormData);
        setFormIndex(formFieldIndexes.formIndex);
        setFieldIndex(formFieldIndexes.fieldIndex);

        // No form registered in state
        if (formFieldIndexes.formIndex < 0) {
            return;
        }

        // Widget already registered in state
        if (formFieldIndexes.formIndex > -1 && formFieldIndexes.fieldIndex > -1) {
            return;
        }

        const { isValid, errorMessage } = ValidateSelectValue();
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "",
                label: hasLabel ? label : key,
                isRequired,
                value: defaultSelectValue,
                type: "DynamicFormSelect",
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
    };

    const ValidateSelectValue = () => {
        const errorMessage =
            isRequired && selectValue.length < 1
                ? (siteMessage("Field_Required", hasLabel ? label : key) as string)
                : "";

        setSelectErrorMessage(errorMessage);

        return {
            isValid: !errorMessage,
            errorMessage,
        };
    };

    const selectChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = event.target.value;
        setSelectValue(newValue);
        if (typeof handleOnChange !== "undefined") {
            handleOnChange(event);
        }
    };

    const getDisplayValidationErrors = () => {
        const formIndex = determineFormIndex(dynamicFormContext.formName, dynamicFormData).formIndex;
        if (formIndex < 0) {
            return false;
        }

        return dynamicFormData?.forms[formIndex]?.displayValidationErrors;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Select
                {...styles.topicsSelect}
                hasLabel={hasLabel}
                label={hasLabel ? label : undefined}
                required={isRequired}
                value={selectValue || ""}
                error={getDisplayValidationErrors() && selectErrorMessage}
                onChange={selectChangeHandler}
            >
                <option value="">{hintText}</option>
                {options.map((option, index) => (
                    <option key={index} value={option.fields.value}>
                        {option.fields.label}
                    </option>
                ))}
            </Select>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicFormSelect);
